import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Client from '../models/serviceModels/Client';
import { getClientById } from '../services/clientsService';
import { v4 as uuidv4 } from 'uuid';
import { ParticipantSearch } from '../components/Search/ParticipantSearch';
import ApiErrorMessages from '../components/UI/ApiErrorMessages';
import Loader from '../components/UI/Loader';
import { MeContext, OrganizationContext } from '../App';
import MergeSearchResultsTable from '../components/Search/MergeSearchResultsTable';
import { format } from 'date-fns';
import { parseJSONDateWithoutTime } from '../utils/dateHelpers';
import toast from "react-hot-toast";
import Button from '../components/UI/Button';
import TruncatedText from '../components/UI/TruncatedText';
import { getDisplayGenderName, getDisplayRaceNames, getEthnicityFieldVisible, getRaceFieldLabel, isDemographicVisible } from '../utils/demographicsSchema';

const ParticipantMergeSearch: React.FC = () => {
  const me = useContext(MeContext);
  const organization = useContext(OrganizationContext);
  const { mergeId = '' } = useParams();
  const [initialClient, setInitialClient] = useState<Client>();
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<any>();
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const navigate = useNavigate();
  const showGender = isDemographicVisible(organization.value, 'gender');
  const showEthnicity = getEthnicityFieldVisible(organization.value);
  const showRace = isDemographicVisible(organization.value, 'race');
  const showAddress = isDemographicVisible(organization.value, 'address');
  const showCity = isDemographicVisible(organization.value, 'city');
  const showState = isDemographicVisible(organization.value, 'state');
  const showZip = isDemographicVisible(organization.value, 'zip');
  const showPhone = isDemographicVisible(organization.value, 'phone');
  const showEmail = isDemographicVisible(organization.value, 'email');
  const raceFieldLabel = getRaceFieldLabel(organization.value);

  useEffect(() => {
    document.title = "Merge Participants"
  }, []);

  useEffect(() => {
    if (mergeId) {
      const stored = localStorage.getItem(`mergeParticipants_${mergeId}`);
      if (stored) {
        const mergeSelecction = JSON.parse(stored) as { selectedClientId?: string, additionalClientIds: string[] };
        if (mergeSelecction?.selectedClientId) {
          getClientById({
            id: mergeSelecction.selectedClientId
          }).then(result => {
            setInitialClient(result);
          }).catch(e => {
            console.error(e);
            setApiError(e);
            toast.error("Failed to load selected participant");
          }).finally(() => {
            setLoading(false);
          });
        } else {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    } else {
      const localMergeId = uuidv4();

      localStorage.setItem(`mergeParticipants_${localMergeId}`, JSON.stringify({
        selectedClientId: undefined,
        additionalClientIds: [],
        expiresOn: new Date().getTime() + (1000 * 60 * 60 * 24 * 5)
      }));

      navigate(`/participants/merge/${localMergeId}/search`, {
        replace: true
      });
    }
  }, [mergeId]);

  if (me.loading || loading) {
    return <Loader />
  }

  return (
    <div className="section">
      <section id="main-content" tabIndex={-1}>
        <div className="row justify-content-center">
          <div className="column-medium-10">
            <div className="row justify-content-center">
              <div className="column-auto">
                <h1>Select Participants to Merge</h1>
              </div>
            </div>
            <ApiErrorMessages error={apiError} />
            <ApiErrorMessages error={me.error} />
            {initialClient && <div className="border padding-5 margin-bottom-5">
              <h2>Selected Participant</h2>
              <div className="row margin-vertical-5 align-items-center">
                {showPhone && <div className="column-large-3">
                  <Link to={`/participants/${initialClient.id}/profile-demographics`} target="_blank">{initialClient.lastName}, {initialClient.firstName}</Link>
                </div>}
              </div>
              <div className="row margin-vertical-5 align-items-center">
                <div className="column-large-3">
                  <label>Created</label>
                  <div>{(initialClient.created && format(new Date(initialClient.created), 'Pp'))}</div>
                </div>
                <div className="column-large-3">
                  <label>Last Updated</label>
                  <div>{(initialClient.updated && format(new Date(initialClient.updated), 'Pp'))}</div>
                </div>
                <div className="column-large-3">
                  <label>Birth Date</label>
                  <div>{initialClient.dateOfBirth && format(parseJSONDateWithoutTime(initialClient.dateOfBirth), 'MM/dd/yyyy')}</div>
                </div>
                <div className="column-large-3">
                  <label>Phone Number</label>
                  <div>{initialClient.phoneNumber}</div>
                </div>
              </div>
              <div className="row margin-top-2">
                <div className="column-auto">
                  <Button id="show-more-details" text={showMoreDetails ? 'Hide More Details' : 'Show More Details'}
                    icon={showMoreDetails ? (<i className="ri-arrow-up-s-line" />) : (<i className="ri-arrow-down-s-line" />)}
                    iconLocation="end"
                    className="secondary"
                    onClick={() => setShowMoreDetails(!showMoreDetails)} />
                </div>
              </div>
              {showMoreDetails && <>
                <div className="row margin-vertical-3 align-items-top">
                  {showEmail && <div className="column-large-3">
                    <label>Email</label>
                    <div className="fw-200 wrap-text">{initialClient.email}</div>
                  </div>}
                  {showEthnicity && <div className="column-large-3">
                    <label>Ethnicity</label>
                    <div>{initialClient.ethnicityName}</div>
                  </div>}
                  {showGender && <div className="column-large-3">
                    <label>Gender</label>
                    <div>{getDisplayGenderName(initialClient.genderName, organization.value)}</div>
                  </div>}
                  {showRace && <div className="column-large-3">
                    <label>{raceFieldLabel}</label>
                    <div><TruncatedText id="races-text" maxLength={30} value={getDisplayRaceNames({
                      ethnicityName: initialClient.ethnicityName,
                      races: initialClient.races
                    }, organization.value).join(", ")} /></div>
                  </div>}
                </div>
                <div className="row margin-vertical-3 align-items-top">
                  {(showAddress || showCity || showState || showZip) && <div className="column-large-3">
                    {showAddress && <><label>Address</label><div>{initialClient.addressLine1}</div>{initialClient.addressLine2 && <div>{initialClient.addressLine2}</div>}</>}
                    {(showCity || showState || showZip) && <div>{showCity && initialClient.addressCity}{showCity && (showState || showZip) && ', '}{showState && initialClient.addressState}{showState && showZip && ' '}{showZip && initialClient.addressZipCode}</div>}
                  </div>}
                </div>
              </>}
            </div>}
            <h2>Search for Participants to Merge</h2>
            {!me.error && <ParticipantSearch>
              <MergeSearchResultsTable />
            </ParticipantSearch>}
          </div>
        </div>
      </section >
    </div >
  );
};

export default ParticipantMergeSearch; 
